"use client";

import { Tag } from "@carbon/react";
import NextLink from "next/link";
import type { MouseEvent } from "react";
import { Card } from "@/components/Card";
import { ClaimEditedLink } from "@/components/ClaimEditedLink";
import {
  claimConfidenceLabel,
  claimStatusKind,
  claimStatusLabel,
  showClaimConfidence,
  wikiConflictChipLabel,
} from "@/lib/ui/wiki";

type WikiClaimCardProps = {
  claimId: string;
  text: string;
  edited?: boolean;
  status: string;
  confidence: number;
  sourceHref: string;
  sourceTitle: string;
  conflictCount?: number;
  selected?: boolean;
  onSelect?: () => void;
};

function stopSelect(event: MouseEvent) {
  event.stopPropagation();
}

export function WikiClaimCard({
  claimId,
  text,
  edited = false,
  status,
  confidence,
  sourceHref,
  sourceTitle,
  conflictCount = 0,
  selected = false,
  onSelect,
}: WikiClaimCardProps) {
  const selectable = Boolean(onSelect);
  const className = [
    "wiki-fact-card",
    selectable ? "is-selectable" : "",
    selected ? "is-selected" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Card
      variant="review"
      className={className}
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
    >
      <header className="wiki-review-card-head">
        <Tag type={claimStatusKind(status)} size="sm">
          {claimStatusLabel(status)}
        </Tag>
        {conflictCount > 0 ? (
          <Tag type="gray" size="sm">
            {wikiConflictChipLabel(conflictCount)}
          </Tag>
        ) : null}
        {showClaimConfidence(confidence) ? (
          <span className="wiki-review-card-confidence">
            {claimConfidenceLabel(confidence)}
          </span>
        ) : null}
        {edited ? (
          <span onClick={selectable ? stopSelect : undefined}>
            <ClaimEditedLink claimId={claimId} compact />
          </span>
        ) : null}
      </header>
      <p className="wiki-claim-text">{text}</p>
      <p className="wiki-fact-source">
        <NextLink
          href={sourceHref}
          onClick={selectable ? stopSelect : undefined}
        >
          {sourceTitle}
        </NextLink>
      </p>
    </Card>
  );
}
